/*
 * TEFCO — B2B behaviour: Quick Order and Compare.
 *
 * Both work against real Shopify endpoints with no app installed:
 *   /search/suggest.json  — SKU and product lookup
 *   /cart/add.js          — multi-line add to cart
 *   localStorage          — the compare set
 *
 * Everything degrades: the sections render server-side, the reorder lists are
 * real links, and nothing here is required to browse or buy.
 */
(function () {
  'use strict';

  var TEFCO = window.TEFCO || {};
  window.TEFCO = TEFCO;

  function money(cents) {
    // Shopify gives suggest.json prices as decimal strings already formatted
    // per shop locale in `price`; fall back to a plain join if absent.
    return cents == null ? '' : cents;
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var a = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, a); }, ms);
    };
  }

  /* Search products/variants by SKU or title. Returns [{id,title,sku,price,available,url,image}] */
  function lookup(term, limit) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(term) +
              '&resources[type]=product&resources[limit]=' + (limit || 6) +
              '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.sku,vendor';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var items = (j.resources && j.resources.results && j.resources.results.products) || [];
        return items.map(function (p) {
          // suggest.json returns the matching variant when the query hits a SKU
          var v = (p.variants && p.variants[0]) || {};
          return {
            variantId: v.id || null,
            productTitle: p.title,
            variantTitle: v.title && v.title.indexOf('Default') === -1 ? v.title : '',
            sku: v.sku || '',
            price: v.price || p.price || '',
            available: p.available !== false,
            url: p.url,
            image: (p.featured_image && p.featured_image.url) || p.image || ''
          };
        });
      });
  }

  /* ============================ QUICK ORDER ============================ */
  class TefcoQuickOrder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.rowsWrap = this.querySelector('[data-qo-rows]');
      this.template = this.querySelector('[data-qo-row]');
      this.msg = this.querySelector('[data-qo-msg]');
      this.summary = this.querySelector('[data-qo-summary]');
      this.note = this.querySelector('[data-qo-note]');
      this.addBtn = this.querySelector('[data-qo-add]');
      this.countEl = this.querySelector('[data-qo-count]');

      this.bindRow(this.template);
      this.bindGlobal();
      this.refresh();
    }

    /* ---- rows ---- */
    bindGlobal() {
      var self = this;
      var addRow = this.querySelector('[data-qo-addrow]');
      if (addRow) addRow.addEventListener('click', function () { self.addRow(); });

      var parse = this.querySelector('[data-qo-parse]');
      if (parse) parse.addEventListener('click', function () {
        var ta = self.querySelector('[data-qo-paste]');
        if (ta) self.ingest(self.parseList(ta.value));
      });

      var csv = this.querySelector('[data-qo-csv]');
      if (csv) csv.addEventListener('change', function () {
        var f = csv.files && csv.files[0];
        if (!f) return;
        var nameEl = self.querySelector('[data-qo-csvname]');
        if (nameEl) nameEl.textContent = f.name;
        var reader = new FileReader();
        reader.onload = function () { self.ingest(self.parseList(String(reader.result))); };
        reader.onerror = function () { self.say('Could not read that file.', 'error'); };
        reader.readAsText(f);
      });

      if (this.addBtn) this.addBtn.addEventListener('click', function () { self.submit(); });

      var clear = this.querySelector('[data-qo-clear]');
      if (clear) clear.addEventListener('click', function () { self.reset(); });

      // reorder checkboxes
      this.addEventListener('change', function (e) {
        if (e.target.matches('[data-qo-reorder]')) self.refresh();
      });
    }

    addRow(prefill) {
      var row = this.template.cloneNode(true);
      var n = this.rowsWrap.querySelectorAll('[data-qo-row]').length + 1;
      row.querySelectorAll('input').forEach(function (i) {
        var id = i.id ? i.id.replace(/-\d+$/, '-' + n) : '';
        if (id) {
          var lab = row.querySelector('label[for="' + i.id + '"]');
          i.id = id;
          if (lab) lab.setAttribute('for', id);
        }
        if (i.hasAttribute('data-qo-sku')) i.value = prefill ? prefill.sku : '';
        if (i.hasAttribute('data-qo-qty')) i.value = prefill ? prefill.qty : 1;
      });
      var m = row.querySelector('[data-qo-match]');
      if (m) m.innerHTML = '<span class="tefco-qo__hint">Start typing a part number.</span>';
      row.removeAttribute('data-resolved');
      var panel = row.querySelector('[data-qo-acpanel]');
      if (panel) { panel.hidden = true; panel.innerHTML = ''; }
      this.rowsWrap.appendChild(row);
      this.bindRow(row);
      if (prefill) this.resolve(row, prefill.sku, prefill.qty);
      this.refresh();
      return row;
    }

    bindRow(row) {
      var self = this;
      var sku = row.querySelector('[data-qo-sku]');
      var qty = row.querySelector('[data-qo-qty]');
      var del = row.querySelector('[data-qo-del]');
      var panel = row.querySelector('[data-qo-acpanel]');

      if (sku) {
        var run = debounce(function () {
          var term = sku.value.trim();
          if (term.length < 2) { self.hidePanel(row); return; }
          lookup(term, 6).then(function (items) { self.showPanel(row, items); });
        }, 250);
        sku.addEventListener('input', run);
        sku.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') self.hidePanel(row);
          if (e.key === 'Enter') {
            e.preventDefault();
            var first = panel && panel.querySelector('[data-pick]');
            if (first) first.click();
          }
        });
        sku.addEventListener('blur', function () { setTimeout(function () { self.hidePanel(row); }, 150); });
      }
      if (qty) qty.addEventListener('input', function () {
        if (parseInt(qty.value, 10) < 1) qty.value = 1;
        self.refresh();
      });
      if (del) del.addEventListener('click', function () {
        if (self.rowsWrap.querySelectorAll('[data-qo-row]').length > 1) row.remove();
        else self.clearRow(row);
        self.refresh();
      });
    }

    clearRow(row) {
      var sku = row.querySelector('[data-qo-sku]');
      var qty = row.querySelector('[data-qo-qty]');
      if (sku) sku.value = '';
      if (qty) qty.value = 1;
      row.removeAttribute('data-resolved');
      row.removeAttribute('data-variant');
      var m = row.querySelector('[data-qo-match]');
      if (m) m.innerHTML = '<span class="tefco-qo__hint">Start typing a part number.</span>';
      var lt = row.querySelector('[data-qo-lt]');
      if (lt) lt.textContent = '—';
    }

    showPanel(row, items) {
      var self = this;
      var panel = row.querySelector('[data-qo-acpanel]');
      var sku = row.querySelector('[data-qo-sku]');
      if (!panel) return;
      if (!items.length) {
        panel.innerHTML = '<span class="tefco-ac__eyebrow">No match</span>';
        panel.hidden = false;
        return;
      }
      panel.innerHTML = '<span class="tefco-ac__eyebrow">' + items.length + ' match' + (items.length === 1 ? '' : 'es') + '</span>';
      items.forEach(function (it) {
        var a = document.createElement('button');
        a.type = 'button';
        a.className = 'tefco-ac__row';
        a.setAttribute('data-pick', '1');
        a.style.width = '100%';
        a.style.border = '0';
        a.style.background = 'none';
        a.style.textAlign = 'start';
        a.style.cursor = 'pointer';

        var text = document.createElement('span');
        text.className = 'tefco-ac__text';
        var t = document.createElement('span');
        t.className = 'tefco-ac__t';
        t.textContent = it.sku || it.productTitle;
        var s = document.createElement('span');
        s.className = 'tefco-ac__s';
        s.textContent = it.productTitle + (it.variantTitle ? ' · ' + it.variantTitle : '');
        text.appendChild(t); text.appendChild(s);

        var p = document.createElement('span');
        p.className = 'tefco-ac__price';
        p.textContent = it.price ? String(it.price) : '';

        a.appendChild(text); a.appendChild(p);
        a.addEventListener('click', function () {
          if (sku) sku.value = it.sku || it.productTitle;
          self.apply(row, it);
          self.hidePanel(row);
        });
        panel.appendChild(a);
      });
      panel.hidden = false;
      if (sku) sku.setAttribute('aria-expanded', 'true');
    }

    hidePanel(row) {
      var panel = row.querySelector('[data-qo-acpanel]');
      var sku = row.querySelector('[data-qo-sku]');
      if (panel) { panel.hidden = true; panel.innerHTML = ''; }
      if (sku) sku.setAttribute('aria-expanded', 'false');
    }

    apply(row, item) {
      var m = row.querySelector('[data-qo-match]');
      if (m) {
        m.innerHTML = '';
        var t = document.createElement('span');
        t.className = 'tefco-qo__match-t';
        t.textContent = item.productTitle;
        var s = document.createElement('span');
        s.className = 'tefco-qo__match-s';
        s.textContent = (item.sku ? item.sku : '') + (item.available ? '' : ' · out of stock');
        m.appendChild(t); m.appendChild(s);
      }
      var lt = row.querySelector('[data-qo-lt]');
      if (lt) lt.textContent = item.price ? String(item.price) : '—';
      if (item.variantId) {
        row.setAttribute('data-resolved', '1');
        row.setAttribute('data-variant', item.variantId);
      } else {
        row.removeAttribute('data-resolved');
      }
      this.refresh();
    }

    /* Resolve a SKU from paste/CSV without user interaction. */
    resolve(row, sku, qty) {
      var self = this;
      return lookup(sku, 3).then(function (items) {
        // exact SKU match wins; otherwise first result, flagged as ambiguous
        var exact = items.filter(function (i) {
          return i.sku && i.sku.toLowerCase() === String(sku).toLowerCase();
        })[0];
        var pick = exact || items[0];
        if (!pick) {
          var m = row.querySelector('[data-qo-match]');
          if (m) m.innerHTML = '<span class="tefco-qo__match-s" style="color:var(--tefco-error-solid)">No match for "' + String(sku).replace(/[<>&]/g, '') + '"</span>';
          return;
        }
        self.apply(row, pick);
        if (!exact) {
          var mm = row.querySelector('[data-qo-match]');
          if (mm) {
            var warn = document.createElement('span');
            warn.className = 'tefco-qo__match-s';
            warn.style.color = 'var(--tefco-warning)';
            warn.textContent = 'Closest match — please confirm';
            mm.appendChild(warn);
          }
        }
      });
    }

    /* "SKU, qty" per line — comma, tab, semicolon or whitespace separated. */
    parseList(text) {
      return String(text || '')
        .split(/\r?\n/)
        .map(function (l) { return l.trim(); })
        .filter(Boolean)
        .map(function (l) {
          var parts = l.split(/[,;\t]+|\s{2,}|\s+(?=\d+$)/).filter(Boolean);
          var sku = (parts[0] || '').trim();
          var qty = parseInt((parts[1] || '1').replace(/\D/g, ''), 10) || 1;
          return { sku: sku, qty: qty };
        })
        .filter(function (r) { return r.sku && !/^sku$/i.test(r.sku); }); // drop header row
    }

    ingest(list) {
      var self = this;
      if (!list.length) { this.say('Nothing to match — check the format.', 'error'); return; }
      // clear existing rows, then one row per parsed line
      this.rowsWrap.querySelectorAll('[data-qo-row]').forEach(function (r, i) { if (i > 0) r.remove(); });
      this.clearRow(this.rowsWrap.querySelector('[data-qo-row]'));

      var first = this.rowsWrap.querySelector('[data-qo-row]');
      var firstSku = first.querySelector('[data-qo-sku]');
      var firstQty = first.querySelector('[data-qo-qty]');
      if (firstSku) firstSku.value = list[0].sku;
      if (firstQty) firstQty.value = list[0].qty;
      var jobs = [this.resolve(first, list[0].sku, list[0].qty)];
      for (var i = 1; i < list.length; i++) jobs.push(this.addRowResolved(list[i]));

      Promise.all(jobs).then(function () {
        self.refresh();
        self.say(list.length + ' line' + (list.length === 1 ? '' : 's') + ' matched. Review, then add to cart.', 'success');
      });
    }

    addRowResolved(item) {
      var row = this.addRow({ sku: item.sku, qty: item.qty });
      return this.resolve(row, item.sku, item.qty);
    }

    /* ---- collect + submit ---- */
    collect() {
      var out = [];
      this.rowsWrap.querySelectorAll('[data-qo-row][data-resolved]').forEach(function (row) {
        var id = row.getAttribute('data-variant');
        var q = parseInt(row.querySelector('[data-qo-qty]').value, 10) || 1;
        if (id) out.push({ id: Number(id), quantity: q });
      });
      // reorder-from-history checkboxes
      this.querySelectorAll('[data-qo-reorder]:checked').forEach(function (cb) {
        out.push({ id: Number(cb.getAttribute('data-variant')), quantity: parseInt(cb.getAttribute('data-qty'), 10) || 1 });
      });
      // merge duplicates
      var merged = {};
      out.forEach(function (l) { merged[l.id] = (merged[l.id] || 0) + l.quantity; });
      return Object.keys(merged).map(function (k) { return { id: Number(k), quantity: merged[k] }; });
    }

    refresh() {
      var lines = this.collect();
      var units = lines.reduce(function (a, l) { return a + l.quantity; }, 0);
      if (this.summary) {
        this.summary.textContent = lines.length + ' line' + (lines.length === 1 ? '' : 's');
      }
      if (this.note) {
        this.note.textContent = lines.length ? units + ' unit' + (units === 1 ? '' : 's') + ' total' : 'Match at least one part number.';
      }
      if (this.countEl) {
        var rows = this.rowsWrap.querySelectorAll('[data-qo-row]').length;
        this.countEl.textContent = rows + ' row' + (rows === 1 ? '' : 's');
      }
      if (this.addBtn) this.addBtn.disabled = lines.length === 0;
    }

    submit() {
      var self = this;
      var lines = this.collect();
      if (!lines.length) return;
      this.addBtn.disabled = true;
      var original = this.addBtn.textContent;
      this.addBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Add failed'); });
          return r.json();
        })
        .then(function () {
          self.addBtn.textContent = original;
          self.say(lines.length + ' line' + (lines.length === 1 ? '' : 's') + ' added to your cart.', 'success');
          document.dispatchEvent(new CustomEvent('tefco:cart:added', { bubbles: true }));
          if (TEFCO.toast) {
            TEFCO.toast({ type: 'success', title: 'Added to cart', text: lines.length + ' lines', actionText: 'View cart', actionHref: '/cart' });
          }
        })
        .catch(function (err) {
          self.addBtn.textContent = original;
          self.addBtn.disabled = false;
          self.say(err.message || 'Could not add these lines. Check stock and try again.', 'error');
        });
    }

    reset() {
      var rows = this.rowsWrap.querySelectorAll('[data-qo-row]');
      rows.forEach(function (r, i) { if (i > 0) r.remove(); });
      this.clearRow(rows[0]);
      this.querySelectorAll('[data-qo-reorder]:checked').forEach(function (cb) { cb.checked = false; });
      var ta = this.querySelector('[data-qo-paste]');
      if (ta) ta.value = '';
      var nameEl = this.querySelector('[data-qo-csvname]');
      if (nameEl) nameEl.textContent = '';
      if (this.msg) this.msg.innerHTML = '';
      this.refresh();
    }

    say(text, tone) {
      if (!this.msg) return;
      this.msg.innerHTML = '';
      var d = document.createElement('div');
      d.className = 'tefco-notice' + (tone === 'error' ? ' tefco-notice--error' : tone === 'success' ? ' tefco-notice--success' : '');
      if (tone === 'error') d.setAttribute('role', 'alert');
      var m = document.createElement('span');
      m.className = 'tefco-notice__main';
      m.textContent = text;
      d.appendChild(m);
      this.msg.appendChild(d);
    }
  }

  /* ============================== COMPARE ============================== */
  var CKEY = 'tefco_compare';

  function readSet() {
    try { return JSON.parse(localStorage.getItem(CKEY) || '[]'); } catch (e) { return []; }
  }
  function writeSet(list) {
    try { localStorage.setItem(CKEY, JSON.stringify(list)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('tefco:compare:changed', { detail: { list: list } }));
  }

  TEFCO.compareAdd = function (handle, max) {
    var list = readSet();
    if (list.indexOf(handle) !== -1) return list;
    if (max && list.length >= max) {
      if (TEFCO.toast) TEFCO.toast({ type: 'error', title: 'Compare is full', text: 'Remove a product first.' });
      return list;
    }
    list.push(handle);
    writeSet(list);
    if (TEFCO.toast) TEFCO.toast({ type: 'success', title: 'Added to compare', text: list.length + ' selected', actionText: 'Compare', actionHref: '/pages/compare' });
    return list;
  };
  TEFCO.compareRemove = function (handle) {
    var list = readSet().filter(function (h) { return h !== handle; });
    writeSet(list);
    return list;
  };
  TEFCO.compareClear = function () { writeSet([]); };

  // Delegated toggle for any [data-tefco-compare] control on cards/product pages
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tefco-compare]');
    if (!btn) return;
    e.preventDefault();
    var handle = btn.getAttribute('data-handle');
    if (!handle) return;
    var list = readSet();
    if (list.indexOf(handle) === -1) {
      TEFCO.compareAdd(handle, Number(btn.getAttribute('data-max')) || 6);
      btn.setAttribute('aria-pressed', 'true');
    } else {
      TEFCO.compareRemove(handle);
      btn.setAttribute('aria-pressed', 'false');
    }
  });

  class TefcoCompare extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.slots = Number(this.dataset.slots) || 6;
      this.table = this.querySelector('[data-cmp-table]');
      this.headRow = this.querySelector('[data-cmp-headrow]');
      this.body = this.querySelector('[data-cmp-body]');
      this.scroll = this.querySelector('[data-cmp-scroll]');
      this.emptyEl = this.querySelector('[data-cmp-empty]');
      this.footEl = this.querySelector('[data-cmp-foot]');
      this.countEl = this.querySelector('[data-cmp-count]');
      this.view = 'all';
      this.highlight = true;

      var self = this;
      this.querySelectorAll('[data-cmp-view]').forEach(function (b) {
        b.addEventListener('click', function () {
          self.querySelectorAll('[data-cmp-view]').forEach(function (x) {
            x.classList.toggle('is-active', x === b);
            x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
          });
          self.view = b.getAttribute('data-cmp-view');
          self.applyView();
        });
      });
      var hl = this.querySelector('[data-cmp-highlight]');
      if (hl) hl.addEventListener('change', function () { self.highlight = hl.checked; self.applyView(); });
      var clear = this.querySelector('[data-cmp-clear]');
      if (clear) clear.addEventListener('click', function () { TEFCO.compareClear(); self.render(); });

      document.addEventListener('tefco:compare:changed', function () { self.render(); });
      this.render();
    }

    render() {
      var self = this;
      var handles = readSet().slice(0, this.slots);
      if (this.countEl) this.countEl.textContent = handles.length + ' of ' + this.slots + ' slots used';

      if (!handles.length) {
        if (this.emptyEl) this.emptyEl.hidden = false;
        if (this.scroll) this.scroll.hidden = true;
        if (this.footEl) this.footEl.hidden = true;
        return;
      }
      if (this.emptyEl) this.emptyEl.hidden = true;

      // Fetch each product as JSON — .js endpoint needs no app and no section.
      Promise.all(handles.map(function (h) {
        return fetch('/products/' + h + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        var valid = [];
        products.forEach(function (p, i) { if (p) valid.push({ p: p, handle: handles[i] }); });
        if (!valid.length) {
          if (self.emptyEl) self.emptyEl.hidden = false;
          if (self.scroll) self.scroll.hidden = true;
          return;
        }
        self.paint(valid);
        if (self.scroll) self.scroll.hidden = false;
        if (self.footEl) self.footEl.hidden = false;
      });
    }

    paint(items) {
      var self = this;
      // ---- header
      this.headRow.querySelectorAll('.tefco-cmp__prodth').forEach(function (n) { n.remove(); });
      items.forEach(function (it) {
        var th = document.createElement('th');
        th.scope = 'col';
        th.className = 'tefco-cmp__prodth';

        if (it.p.featured_image) {
          var img = document.createElement('img');
          img.className = 'tefco-cmp__pimg';
          img.src = it.p.featured_image;
          img.alt = '';
          img.loading = 'lazy';
          th.appendChild(img);
        }
        if (it.p.vendor) {
          var v = document.createElement('span');
          v.className = 'tefco-cmp__pvendor';
          v.textContent = it.p.vendor;
          th.appendChild(v);
        }
        var a = document.createElement('a');
        a.className = 'tefco-cmp__pname';
        a.href = '/products/' + it.handle;
        a.textContent = it.p.title;
        th.appendChild(a);

        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'tefco-cmp__premove';
        rm.textContent = 'Remove';
        rm.setAttribute('aria-label', 'Remove ' + it.p.title + ' from comparison');
        rm.addEventListener('click', function () { TEFCO.compareRemove(it.handle); });
        th.appendChild(rm);

        self.headRow.appendChild(th);
      });

      // ---- cells
      this.body.querySelectorAll('.tefco-cmp__row').forEach(function (tr) {
        tr.querySelectorAll('.tefco-cmp__cell').forEach(function (c) { c.remove(); });
        var key = tr.getAttribute('data-cmp-key');
        var values = [];
        items.forEach(function (it) {
          var td = document.createElement('td');
          td.className = 'tefco-cmp__cell';
          var val = self.readSpec(it.p, key);
          td.textContent = val || '—';
          values.push((val || '').toString().trim().toLowerCase());
          tr.appendChild(td);
        });
        // a row "differs" when not every value is identical
        var same = values.every(function (v) { return v === values[0]; });
        tr.classList.toggle('is-diff', !same);
      });
      // group rows span the full width
      this.body.querySelectorAll('.tefco-cmp__groupth').forEach(function (th) {
        th.colSpan = items.length + 1;
      });
      this.applyView();
    }

    /* Read a spec value from the product JSON. Shopify's /products/x.js does not
       include metafields, so we look in the places it does expose and fall back
       to a data-* attribute the product page can publish. */
    readSpec(p, key) {
      if (!key) return '';
      var direct = {
        vendor: p.vendor,
        type: p.type,
        price: p.price != null ? (p.price / 100).toFixed(2) : '',
        available: p.available ? 'Yes' : 'No',
        sku: (p.variants && p.variants[0] && p.variants[0].sku) || ''
      };
      if (direct[key] != null && direct[key] !== '') return direct[key];
      if (p.metafields && p.metafields[key] != null) return p.metafields[key];
      // tags carry "key:value" pairs as a no-app fallback
      var tags = p.tags || [];
      for (var i = 0; i < tags.length; i++) {
        var t = String(tags[i]);
        if (t.toLowerCase().indexOf(key.toLowerCase() + ':') === 0) return t.slice(key.length + 1).trim();
      }
      return '';
    }

    applyView() {
      var diffOnly = this.view === 'diff';
      this.body.querySelectorAll('.tefco-cmp__row').forEach(function (tr) {
        var isDiff = tr.classList.contains('is-diff');
        tr.hidden = diffOnly && !isDiff;
        tr.classList.toggle('is-highlight', this.highlight);
      }, this);
      // hide a group heading whose rows are all hidden
      var groups = this.body.querySelectorAll('.tefco-cmp__grouprow');
      groups.forEach(function (g) {
        var any = false, n = g.nextElementSibling;
        while (n && !n.classList.contains('tefco-cmp__grouprow')) {
          if (n.classList.contains('tefco-cmp__row') && !n.hidden) any = true;
          n = n.nextElementSibling;
        }
        g.hidden = !any;
      });
    }
  }


  /* ============================ MULTI-STEP WIZARD ============================
   * <tefco-wizard> — powers the Credit Application and RMA claim.
   *
   * §6 collapse rule 2: "Long form -> numbered steps with sticky progress and
   * pinned Continue. Draft saves per step."
   *
   * Draft saving is real and needs no backend: field values are written to
   * localStorage on change and restored on load, so a half-finished credit
   * application survives a closed tab. Nothing sensitive is sent anywhere —
   * it stays in the customer's own browser until they submit the form.
   *
   * With JS off, every step is visible at once and the form still submits as a
   * single long form, which is why the steps are plain fieldsets.
   */
  class TefcoWizard extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.key = 'tefco_draft_' + (this.dataset.draftKey || 'form');
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-wz-step]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-wz-dot]'));
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-wz-status]');
      this.index = 0;

      if (!this.steps.length) return;
      this.classList.add('is-enhanced');

      var self = this;
      this.addEventListener('click', function (e) {
        var next = e.target.closest('[data-wz-next]');
        var prev = e.target.closest('[data-wz-prev]');
        var jump = e.target.closest('[data-wz-goto]');
        if (next) { e.preventDefault(); self.go(self.index + 1, true); }
        else if (prev) { e.preventDefault(); self.go(self.index - 1); }
        else if (jump) { e.preventDefault(); self.go(Number(jump.getAttribute('data-wz-goto')) || 0); }
      });

      this.restore();
      if (this.form) {
        this.form.addEventListener('input', function () { self.saveSoon(); });
        this.form.addEventListener('change', function () { self.saveSoon(); });
        // Clear the draft once it has actually been submitted.
        this.form.addEventListener('submit', function () {
          try { localStorage.removeItem(self.key); } catch (err) {}
        });
      }
      this.go(0);
    }

    go(i, validate) {
      if (i < 0 || i >= this.steps.length) return;

      // Only block forward movement, and only on real HTML validity.
      if (validate && i > this.index) {
        var current = this.steps[this.index];
        var bad = null;
        current.querySelectorAll('input, select, textarea').forEach(function (f) {
          if (!bad && f.willValidate && !f.checkValidity()) bad = f;
        });
        if (bad) {
          bad.reportValidity();
          bad.focus();
          return;
        }
      }

      this.index = i;
      this.steps.forEach(function (s, n) { s.hidden = n !== i; });
      this.dots.forEach(function (d, n) {
        d.classList.toggle('is-current', n === i);
        d.classList.toggle('is-done', n < i);
        d.setAttribute('aria-current', n === i ? 'step' : 'false');
      });

      // Move focus to the step heading so screen readers follow the change.
      var h = this.steps[i].querySelector('[data-wz-heading]');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
      this.steps[i].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    saveSoon() {
      var self = this;
      clearTimeout(this._t);
      this._t = setTimeout(function () { self.save(); }, 800);
    }

    save() {
      if (!this.form) return;
      var data = {};
      new FormData(this.form).forEach(function (v, k) {
        if (typeof v === 'string') data[k] = v;   // never persist File objects
      });
      try {
        localStorage.setItem(this.key, JSON.stringify({ at: Date.now(), data: data }));
        this.say('Draft saved.');
      } catch (e) {
        this.say('Draft could not be saved in this browser.');
      }
    }

    restore() {
      var raw;
      try { raw = localStorage.getItem(this.key); } catch (e) { return; }
      if (!raw) return;
      var parsed;
      try { parsed = JSON.parse(raw); } catch (e) { return; }
      if (!parsed || !parsed.data) return;

      var form = this.form;
      if (!form) return;
      Object.keys(parsed.data).forEach(function (k) {
        var els = form.querySelectorAll('[name="' + k.replace(/"/g, '\\"') + '"]');
        els.forEach(function (el) {
          if (el.type === 'file') return;
          if (el.type === 'radio' || el.type === 'checkbox') el.checked = el.value === parsed.data[k];
          else el.value = parsed.data[k];
        });
      });
      this.say('Draft restored from this browser.');
    }

    say(text) {
      if (this.status) this.status.textContent = text;
    }
  }

  if (!customElements.get('tefco-wizard')) customElements.define('tefco-wizard', TefcoWizard);

  if (!customElements.get('tefco-quick-order')) customElements.define('tefco-quick-order', TefcoQuickOrder);
  if (!customElements.get('tefco-compare')) customElements.define('tefco-compare', TefcoCompare);
})();
